/**
 * services/pmEngine.ts · PM 预防维修自动触发 v350 Sprint 9-A
 *
 * 三级厂痛点: 印刷机/模切机不停坏, 小厂没钱备件
 * 解法: 3 触发条件自动建维修工单
 *
 * 触发条件 (任一满足):
 *   1. 运行小时累计 ≥ next_maint_hours (从 work_reports.duration_minutes 累加 / 60)
 *   2. 生产数量累计 ≥ next_maint_qty (从 work_reports.qty_good 累加)
 *   3. 日历日期 ≥ next_maint_date (按使用周期)
 *
 * 安全:
 *   - 已有 active maintenance_tasks (status NOT IN completed/closed) 跳过 · 防重复建单
 *   - audit_events log 每次触发 · 透明可追溯
 *   - admin / maintenance / gm / boss 角色才跑 · 防普通用户触发
 *
 * 调度:
 *   - tn-user-login + setTimeout 8s + setInterval 30 min
 *   - 单次手动 runPmCheck() 也可
 */

import { query, getDb, saveDb } from '@core/db';
import { sendNotification } from './notify';
import { getCurrentUser } from '@core/auth';

export interface PmTriggerResult {
  equipmentCode: string;
  equipmentName: string;
  triggerReason: 'hours' | 'qty' | 'date';
  detail: string;
  taskId?: number;
}

export interface PmCheckSummary {
  scanned: number;
  triggered: PmTriggerResult[];
  skipped: number;
  errors: number;
  durationMs: number;
}

const ALLOWED_ROLES = ['admin', 'gm', 'boss', 'maintenance'];

/**
 * 主入口 · 检查所有设备 · 触发条件满足自动建工单
 */
export function runPmCheck(): PmCheckSummary {
  const start = Date.now();
  const summary: PmCheckSummary = {
    scanned: 0,
    triggered: [],
    skipped: 0,
    errors: 0,
    durationMs: 0,
  };

  try {
    const equipment = query<{
      id: number;
      code: string;
      name: string;
      next_maint_date: string | null;
      next_maint_hours: number | null;
      next_maint_qty: number | null;
      last_maint_date: string | null;
    }>(`
      SELECT id, code, name, next_maint_date, next_maint_hours, next_maint_qty, last_maint_date
      FROM equipment
      WHERE COALESCE(deleted_at,'') = '' AND COALESCE(is_active,1) = 1
    `);
    summary.scanned = equipment.length;

    const today = new Date().toISOString().slice(0, 10);

    for (const eq of equipment) {
      try {
        // 1. 检查是否已有 active 维修任务 (防重复)
        const existing = query<{ c: number }>(`
          SELECT COUNT(*) AS c FROM maintenance_tasks
          WHERE equipment_id = ? AND COALESCE(deleted_at,'')='' AND COALESCE(status,'pending') NOT IN ('completed', 'closed', 'done')
        `, [eq.id])[0]?.c || 0;
        if (existing > 0) {
          summary.skipped += 1;
          continue;
        }

        // 2. 检查 3 触发条件
        const trigger = checkPmTrigger(eq, today);
        if (!trigger) {
          summary.skipped += 1;
          continue;
        }

        // 3. 自动建维修工单
        const taskId = autoCreateMaintenanceTask(eq, trigger);
        summary.triggered.push({
          equipmentCode: eq.code,
          equipmentName: eq.name,
          triggerReason: trigger.reason,
          detail: trigger.detail,
          taskId,
        });

        // 4. push 通知
        sendNotification(['maintenance', 'gm'], {
          title: `🔧 PM 自动触发: ${eq.code}`,
          body: `${eq.name} · ${trigger.detail}`,
          severity: 'warning',
          refType: 'maintenance_task',
          refId: taskId,
          refHash: `maintenanceList`,
        });
      } catch (err) {
        summary.errors += 1;
        // eslint-disable-next-line no-console
        console.warn('[pmEngine] equipment scan error:', eq.code, err);
      }
    }

    // 5. saveDb (异步 · 不等)
    if (summary.triggered.length > 0) {
      void saveDb();
    }
  } catch (err) {
    summary.errors += 1;
    // eslint-disable-next-line no-console
    console.error('[pmEngine] runPmCheck error:', err);
  }

  summary.durationMs = Date.now() - start;
  return summary;
}

interface TriggerCheck {
  reason: 'hours' | 'qty' | 'date';
  detail: string;
}

function checkPmTrigger(
  eq: {
    id: number;
    next_maint_date: string | null;
    next_maint_hours: number | null;
    next_maint_qty: number | null;
    last_maint_date: string | null;
  },
  today: string,
): TriggerCheck | null {
  // 条件 3: 日历日期 (最先, 简单)
  if (eq.next_maint_date && eq.next_maint_date <= today) {
    return {
      reason: 'date',
      detail: `计划维修日期 ${eq.next_maint_date} 已到 (今日 ${today})`,
    };
  }

  // 条件 1: 运行小时累计 (从 last_maint_date 算起)
  if (eq.next_maint_hours && eq.next_maint_hours > 0) {
    const sinceClause = eq.last_maint_date
      ? `AND DATE(report_time) > '${eq.last_maint_date}'`
      : '';
    const runHours = query<{ h: number }>(`
      SELECT COALESCE(SUM(duration_minutes), 0) / 60.0 AS h
      FROM work_reports
      WHERE equipment_id = ? ${sinceClause}
    `, [eq.id])[0]?.h || 0;
    if (runHours >= eq.next_maint_hours) {
      return {
        reason: 'hours',
        detail: `运行 ${runHours.toFixed(1)}h ≥ 阈值 ${eq.next_maint_hours}h`,
      };
    }
  }

  // 条件 2: 生产数量累计
  if (eq.next_maint_qty && eq.next_maint_qty > 0) {
    const sinceClause = eq.last_maint_date
      ? `AND DATE(report_time) > '${eq.last_maint_date}'`
      : '';
    const totalQty = query<{ q: number }>(`
      SELECT COALESCE(SUM(qty_good), 0) AS q
      FROM work_reports
      WHERE equipment_id = ? ${sinceClause}
    `, [eq.id])[0]?.q || 0;
    if (totalQty >= eq.next_maint_qty) {
      return {
        reason: 'qty',
        detail: `产量 ${totalQty} ≥ 阈值 ${eq.next_maint_qty}`,
      };
    }
  }

  return null;
}

function autoCreateMaintenanceTask(
  eq: { id: number; code: string; name: string },
  trigger: TriggerCheck,
): number {
  const db = getDb();
  const code = `PM-${new Date().toISOString().slice(0, 10).replace(/-/g, '')}-${eq.code}`;
  const title = `PM 预防维修 · ${eq.name}`;
  const body = `自动触发 · ${trigger.detail} · 请按维修 SOP 检查保养`;

  db.run(`
    INSERT INTO maintenance_tasks (code, equipment_id, priority, status, request_type, title, description, created_at)
    VALUES (?, ?, 'medium', 'pending', 'preventive', ?, ?, datetime('now'))
  `, [code, eq.id, title, body]);

  const lastId = query<{ id: number }>(`SELECT last_insert_rowid() AS id`)[0]?.id || 0;

  // 写 audit_events
  try {
    db.run(`
      INSERT INTO audit_events (module, action, ref_type, ref_id, body, severity, created_at)
      VALUES ('maintenance', 'pm_auto_trigger', 'maintenance_task', ?, ?, 'info', datetime('now'))
    `, [lastId, JSON.stringify({ equipment: eq.code, trigger: trigger.reason, detail: trigger.detail })]);
  } catch {
    // audit_events schema 可能不全 · 静默 (R12 严守)
  }

  return lastId;
}

/**
 * 后台调度 · main.ts 调用
 */
export function startPmScanner(): void {
  // 用户登录后启动
  document.addEventListener('tn-user-login', () => {
    const user = getCurrentUser();
    if (!user || !ALLOWED_ROLES.includes(user.role)) return;
    setTimeout(() => safeRun(), 8000);
    setInterval(() => safeRun(), 30 * 60 * 1000); // 30 min
  });
}

function safeRun(): void {
  try {
    const result = runPmCheck();
    if (result.triggered.length > 0) {
      // eslint-disable-next-line no-console
      console.log(`[pmEngine] auto-triggered ${result.triggered.length} PM tasks`);
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[pmEngine] scan error:', err);
  }
}

/**
 * 公开 API · 手动触发 (admin 页按钮调)
 */
export const TN_PM_ENGINE = {
  run: runPmCheck,
  start: startPmScanner,
};
